import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ShoppingBag, Star, Truck, Shield, RefreshCw } from "lucide-react";
import { http } from "../../lib/api";
import { useCart } from "../../context/CartContext";
import { toast } from "sonner";

function ProductCard({ product }) {
  const { addItem } = useCart();
  const [adding, setAdding] = useState(false);

  const handleQuickAdd = (e) => {
    e.preventDefault();
    if (product.variants?.length > 0) { return; }
    setAdding(true);
    addItem(product, null, 1);
    toast.success(`${product.name} added to cart!`);
    setTimeout(() => setAdding(false), 800);
  };

  const isOutOfStock = product.stock !== undefined && product.stock === 0;
  const displayPrice = product.price != null ? `${product.currency || "$"}${Number(product.price).toFixed(2)}` : "";
  const comparePrice = product.compare_price ? `${product.currency || "$"}${Number(product.compare_price).toFixed(2)}` : null;

  return (
    <Link to={`/store/product/${product.id}`} className="group block">
      <div className="rounded-2xl overflow-hidden bg-white border border-gray-100 shadow-sm hover:shadow-md transition-shadow">
        <div className="relative aspect-square bg-gray-50 overflow-hidden">
          {product.images?.[0]
            ? <img src={product.images[0]} alt={product.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
            : <div className="w-full h-full grid place-items-center"><ShoppingBag size={40} className="text-gray-300" /></div>
          }
          {product.badge && (
            <span className="absolute top-3 left-3 px-2.5 py-1 rounded-full text-xs font-bold text-white" style={{ background: "#C5A028" }}>{product.badge}</span>
          )}
          {isOutOfStock && (
            <div className="absolute inset-0 bg-white/70 grid place-items-center"><span className="text-sm font-bold text-gray-500">Out of Stock</span></div>
          )}
          {!isOutOfStock && !product.variants?.length && (
            <button
              onClick={handleQuickAdd}
              className="absolute bottom-3 left-1/2 -translate-x-1/2 px-5 py-2 rounded-full text-sm font-semibold text-white shadow-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap"
              style={{ background: "#0F1E38" }}
            >{adding ? "Added ✓" : "Quick Add"}</button>
          )}
        </div>
        <div className="p-4">
          {product.collection_name && (
            <div className="text-xs font-semibold uppercase tracking-widest text-yellow-600 mb-1">{product.collection_name}</div>
          )}
          <div className="font-semibold text-gray-900 leading-tight line-clamp-2 mb-2">{product.name}</div>
          <div className="flex items-center gap-2">
            <span className="font-bold text-gray-900">{displayPrice}</span>
            {comparePrice && <span className="text-sm text-gray-400 line-through">{comparePrice}</span>}
          </div>
          {product.variants?.length > 0 && (
            <div className="text-xs text-gray-400 mt-1">{product.variants.length} options available</div>
          )}
        </div>
      </div>
    </Link>
  );
}

export default function StoreFront() {
  const [settings, setSettings] = useState({});
  const [collections, setCollections] = useState([]);
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [newArrivals, setNewArrivals] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      http.get("/store/settings").then(r => setSettings(r.data)).catch(() => {}),
      http.get("/store/collections").then(r => setCollections(r.data)).catch(() => {}),
      http.get("/store/products", { params: { featured: true, limit: 8 } }).then(r => setFeaturedProducts(r.data)).catch(() => {}),
      http.get("/store/products", { params: { sort: "newest", limit: 4 } }).then(r => setNewArrivals(r.data)).catch(() => {}),
    ]).finally(() => setLoading(false));
  }, []);

  if (loading) return (
    <div className="space-y-8 animate-pulse">
      <div className="h-96 rounded-3xl bg-gray-100" />
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[1,2,3,4].map(i => <div key={i} className="h-64 rounded-2xl bg-gray-100" />)}
      </div>
    </div>
  );

  const hero = settings.hero || {};

  return (
    <div className="space-y-14">
      {/* Hero Banner */}
      <section
        className="relative overflow-hidden rounded-3xl min-h-[380px] md:min-h-[480px] flex items-center"
        style={{ background: hero.bg_color || "linear-gradient(135deg, #0F1E38 0%, #1a3060 100%)" }}
      >
        {hero.image_url && (
          <img src={hero.image_url} alt="" className="absolute inset-0 w-full h-full object-cover opacity-30" />
        )}
        <div className="relative z-10 px-8 md:px-16 py-14 max-w-2xl">
          {hero.label && (
            <div className="inline-block text-xs font-bold uppercase tracking-[0.22em] px-3 py-1 rounded-full mb-5 bg-white/15 text-yellow-400">{hero.label}</div>
          )}
          <h1 className="text-3xl md:text-5xl font-bold text-white leading-tight mb-4" style={{ fontFamily: "Cormorant Garamond, serif" }}>
            {hero.heading || "Shop the CPM Collection"}
          </h1>
          <p className="text-white/80 text-base md:text-lg leading-relaxed mb-8">
            {hero.subheading || "Curated products for the Celestial community — faith, culture, and community in every item."}
          </p>
          <div className="flex flex-wrap gap-3">
            <Link to="/store/catalog" className="inline-flex items-center gap-2 px-6 py-3 rounded-full font-bold text-sm text-white transition-opacity hover:opacity-90" style={{ background: "#C5A028" }}>
              {hero.cta_label || "Shop Now"} <ArrowRight size={16} />
            </Link>
            {hero.secondary_cta_label && hero.secondary_cta_link && (
              <Link to={hero.secondary_cta_link} className="inline-flex items-center gap-2 px-6 py-3 rounded-full font-bold text-sm text-white border border-white/30 hover:bg-white/10 transition-colors">
                {hero.secondary_cta_label}
              </Link>
            )}
          </div>
        </div>
      </section>

      {/* Trust badges */}
      <section className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { icon: Truck, label: settings.badge_shipping || "Free Shipping", sub: settings.badge_shipping_sub || "On orders over $50" },
          { icon: Shield, label: settings.badge_secure || "Secure Payment", sub: "Stripe & PayPal" },
          { icon: RefreshCw, label: settings.badge_returns || "Easy Returns", sub: settings.badge_returns_sub || "30-day return policy" },
          { icon: Star, label: settings.badge_quality || "Curated Quality", sub: "Handpicked for CCC family" },
        ].map(({ icon: Icon, label, sub }) => (
          <div key={label} className="flex items-start gap-3 p-4 bg-white rounded-2xl border border-gray-100 shadow-sm">
            <div className="w-10 h-10 rounded-xl grid place-items-center shrink-0" style={{ background: "rgba(197,160,40,0.12)" }}>
              <Icon size={18} style={{ color: "#C5A028" }} />
            </div>
            <div>
              <div className="font-semibold text-gray-900 text-sm">{label}</div>
              <div className="text-xs text-gray-500 mt-0.5">{sub}</div>
            </div>
          </div>
        ))}
      </section>

      {/* Collections */}
      {collections.length > 0 && (
        <section>
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-gray-900" style={{ fontFamily: "Cormorant Garamond, serif" }}>Shop by Collection</h2>
            <Link to="/store/catalog" className="text-sm font-semibold text-yellow-600 hover:text-yellow-700 flex items-center gap-1">All Products <ArrowRight size={14} /></Link>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {collections.map(c => (
              <Link
                key={c.id}
                to={`/store/catalog?collection=${c.slug}`}
                className="group relative overflow-hidden rounded-2xl aspect-[3/2] bg-gray-100 flex items-end"
              >
                {c.image_url && <img src={c.image_url} alt={c.name} className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />}
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                <div className="relative p-4">
                  <div className="text-white font-bold text-base leading-tight">{c.name}</div>
                  {c.product_count > 0 && <div className="text-white/70 text-xs mt-0.5">{c.product_count} items</div>}
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* Featured Products */}
      {featuredProducts.length > 0 && (
        <section>
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-gray-900" style={{ fontFamily: "Cormorant Garamond, serif" }}>Featured Products</h2>
            <Link to="/store/catalog?featured=true" className="text-sm font-semibold text-yellow-600 hover:text-yellow-700 flex items-center gap-1">View all <ArrowRight size={14} /></Link>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {featuredProducts.map(p => <ProductCard key={p.id} product={p} />)}
          </div>
        </section>
      )}

      {/* New Arrivals */}
      {newArrivals.length > 0 && (
        <section className="rounded-3xl p-8 md:p-12" style={{ background: "#0F1E38" }}>
          <div className="flex items-center justify-between mb-8">
            <div>
              <div className="text-xs font-bold uppercase tracking-widest text-yellow-400 mb-2">Just Arrived</div>
              <h2 className="text-2xl font-bold text-white" style={{ fontFamily: "Cormorant Garamond, serif" }}>New Arrivals</h2>
            </div>
            <Link to="/store/catalog?sort=newest" className="text-sm font-semibold text-yellow-400 hover:text-yellow-300 flex items-center gap-1">See all <ArrowRight size={14} /></Link>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {newArrivals.map(p => <ProductCard key={p.id} product={p} />)}
          </div>
        </section>
      )}

      {/* Community CTA */}
      <section className="text-center py-10 px-6 rounded-3xl bg-white border border-gray-100 shadow-sm">
        <div className="text-4xl mb-4">✞</div>
        <h2 className="text-2xl font-bold text-gray-900 mb-3" style={{ fontFamily: "Cormorant Garamond, serif" }}>Part of the Celestial Family</h2>
        <p className="text-gray-600 max-w-md mx-auto text-sm leading-relaxed mb-6">
          Every purchase supports the CCC community. Connect with brethren, discover parishes, and grow together in faith.
        </p>
        <Link to="/app" className="inline-flex items-center gap-2 px-6 py-3 rounded-full font-bold text-sm text-white" style={{ background: "#0F1E38" }}>
          Go to CPM Community <ArrowRight size={15} />
        </Link>
      </section>
    </div>
  );
}
